import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import { Avatar, Box, ButtonBase, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { authStore } from '../../../stores/authStore';
import { LogoutConfirmDialog } from '../index';

interface UserMenuProps {
  user: User | undefined;
}

const UserMenu = ({
  user,
}: UserMenuProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleCloseUserMenu();
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    authStore.logout();
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleNavigateToProfile = () => {
    handleCloseUserMenu();
    navigate(ROUTES.PROFILE);
  };

  const handleNavigateToSettings = () => {
    handleCloseUserMenu();
    navigate(ROUTES.SETTINGS);
  };

  if (!user) {
    return <></>;
  }

  return (
    <Box sx={{ borderTop: `1px solid rgba(255, 255, 255, 0.1)`, width: '100%' }}>
      <ButtonBase
        onClick={handleOpenUserMenu}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          px: 3,
          py: 2,
          borderRadius: 0,
          transition: theme.transitions.create('background-color', {
            duration: theme.transitions.duration.shortest,
          }),
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        }}
      >
        <Stack textAlign="left" direction="row" spacing={1.5} alignItems="center">
          <Avatar
            alt={user?.name}
            src={user?.avatar}
            sx={{
              width: 34,
              height: 34,
              backgroundColor: theme.palette.secondary.main,
              fontSize: 14,
            }}
          >
            {user?.name?.charAt(0)}
          </Avatar>
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.common.white,
                fontWeight: 'medium',
                lineHeight: 1.2
              }}
            >
              {t('menu.profile', { ns: 'user' })}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 150,
                mt: 0.2
              }}
            >
              {user.email || t('anonymous', { ns: 'user' })}
            </Typography>
          </Box>
        </Stack>
        <KeyboardArrowRightIcon
          fontSize="small"
          sx={{
            color: theme.palette.common.white,
            opacity: 0.5,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseUserMenu}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              width: 220,
              overflow: 'visible',
              ml: 1.5,
            }
          }
        }}
      >
        <MenuItem onClick={handleNavigateToProfile}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('menu.profile', { ns: 'user' })}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleNavigateToSettings}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('menu.settings', { ns: 'user' })}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogoutClick}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.error.main }}>
            {t('menu.logout', { ns: 'user' })}
          </ListItemText>
        </MenuItem>
      </Menu>

      <LogoutConfirmDialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
      />
    </Box>
  );
};

export default UserMenu; 